// frontend/src/config/userGuideContent.js
//
// Content source for the in-app User Guide (frontend/src/pages/UserGuide.js).
// Kept as plain data so HR can update copy/screenshots without touching the
// page component. Icons are referenced by name and resolved to lucide-react
// components in the page (config stays serialisable / logic-free).
//
// Section shape:
//   {
//     id:        string  - stable anchor id (used for #deep-links + scroll)
//     title:     string
//     icon:      string  - lucide-react icon name (see ICONS map in UserGuide.js)
//     color:     string  - palette key (blue|indigo|teal|violet|amber|green|rose)
//     audience:  string  - visibility key (see AUDIENCE matchers in UserGuide.js)
//     translationStatus?:'english-fallback' while new copy awaits native review
//     summary:   string  - one-line description shown in the quick-nav card
//     route:     string? - if set, an "Open this feature" button links here
//     routeLabel:string?
//     blocks:    Block[]
//   }
//
// Block types:
//   { type: 'steps',  heading?, items: string[] }   - numbered walkthrough
//   { type: 'list',   heading?, items: string[] }    - bulleted facts
//   { type: 'note',   tone: 'info'|'tip'|'warning', text: string }
//   { type: 'image',  alt: string, caption?: string }- placeholder screenshot slot

export const SUPPORT_EMAIL = 'hrm@winner-e.sg';

export const USER_GUIDE_SECTIONS = [
  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'Rocket',
    color: 'blue',
    audience: 'everyone',
    summary: 'Find your way around, install the app on your phone, and manage your account.',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'You are already signed in — the hard part is done. The first time you logged in, the portal walked you through acknowledging the Privacy Policy (PDPA) and setting your own password. This page helps you find your way around from here.',
      },
      {
        type: 'list',
        heading: 'Finding your way around',
        items: [
          'Menu (sidebar): tap the ☰ menu icon (top-left on mobile) to open the list of features. On desktop it stays open on the left.',
          'Notifications: the 🔔 bell at the top-right shows approvals, reminders and updates. A red dot means something needs your attention.',
          'Your account: tap your name/avatar at the top-right to change your password, switch roles (if you have admin/asset access), or sign out.',
          'Quick Actions: the floating round button (bottom-right) gives one-tap shortcuts to common tasks.',
          'Language: use the language selector at the top of this guide to read it in your preferred language (more languages coming soon).',
        ],
      },
      {
        type: 'steps',
        heading: 'Install it like an app (recommended)',
        items: [
          'The portal works as an app on your phone (PWA) — no app store needed.',
          'On Android (Chrome): tap the menu ⋮ → "Add to Home screen" / "Install app".',
          'On iPhone (Safari): tap the Share icon → "Add to Home Screen".',
          'Open it from your home screen for a full-screen, faster experience.',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'Forgot your password? On the login screen, tap "Forgot password?" and enter your work email — you will be emailed a link to set a new one.',
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'Pull down on any page to refresh it and load the latest information.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    icon: 'LayoutDashboard',
    color: 'indigo',
    audience: 'everyone',
    summary: 'Your home screen — a quick snapshot and one-tap shortcuts to what you do most.',
    route: '/dashboard',
    routeLabel: 'Go to Dashboard',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'The Dashboard is your home screen: it opens when you sign in, and you can return any time by tapping the company logo at the top-left. It gives you a quick overview and shortcuts — the cards you see depend on your role.',
      },
      {
        type: 'list',
        heading: 'What everyone sees',
        items: [
          'A greeting at the top with your name.',
          'Recent Leave Applications: your latest requests with their status (Pending, Approved, etc.). Tap the eye icon to view details, or "View all" to open Leave Management.',
          'Leave Balances: a card for each leave type showing days available, days used and your entitlement. Tap "View all" to see the full list.',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'Quick Actions: tap the floating round button (bottom-right of any screen) for one-tap shortcuts — Clock In/Out, Apply for Leave, and Submit a Petty Cash Claim. Staff and directors also get "My Name Card" to share their digital name card.',
      },
      {
        // Approvers (RO / manager / Trade Leader / HOD) — leave-approval inbox.
        type: 'note',
        tone: 'info',
        audience: 'approvers',
        text: 'My Approvals: leave requests waiting for your decision appear in this card, and a count badge on the Dashboard shows how many. See "For Reporting Officers: Approving Leave" for how to approve or reject.',
      },
      {
        // ROs / HODs also get the attendance-exception alert card.
        type: 'list',
        heading: 'Team Alerts (Reporting Officers & HODs)',
        audience: 'reportingLine',
        items: [
          'Team Alerts flags attendance exceptions for your team — for example a worker who clocked in but has no clock-out.',
          'Tap an alert to review and resolve it (e.g. confirm the clock-out time on the worker\'s behalf).',
        ],
      },
      {
        // Worker-only operational cards that appear contextually.
        type: 'note',
        tone: 'info',
        audience: 'workersOnly',
        text: 'Nightwork and Worker Trade Transfer cards appear here when they apply to you — for example when you have an active nightwork assignment, a recovery day (rest day), or a trade-transfer item waiting for your acknowledgement. Tap "View All" on a card to open it. (See the Nightwork and Worker Trade Transfer sections for details.)',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'attendance',
    title: 'Clock In & Clock Out',
    icon: 'Clock',
    color: 'teal',
    audience: 'notExecutiveDirector',
    summary: 'Record your work hours and site location each day.',
    route: '/attendance',
    routeLabel: 'Open Attendance',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Open Attendance from the sidebar ("Attendance — Clock In/Out & Time Tracking"). The page shows a live Singapore-time clock and your activity for the day.',
      },
      {
        type: 'steps',
        heading: 'How to Clock In',
        items: [
          'Tap the green "Clock In" button.',
          'Allow location access when your phone asks — your site is confirmed using GPS.',
          'In the "Select Location" pop-up, pick your project. Nearby projects (within ~300m) appear under the "Nearby" tab; otherwise use "All Projects" or the search box.',
          'If the project has more than one work site, choose your site in the "Select Work Site" pop-up. (If there is only one site, it is selected for you.)',
          'You are clocked in — the button turns red ("Clock Out") and you will see "Active since" with your start time.',
        ],
      },
      {
        type: 'steps',
        heading: 'How to Clock Out',
        items: [
          'Tap the red "Clock Out" button. Fresh GPS is taken automatically.',
          'If you are asked, enter a short Work Description (at least 10 characters) describing what you did today, then tap "Submit & Clock Out".',
          'Workers may be asked about overtime: answer "Yes"/"No", select the time you finished, then confirm. This is what lets you claim OT.',
          'You will see "Clocked out successfully".',
        ],
      },
      {
        type: 'list',
        heading: 'Your activity & summary',
        items: [
          'Activity list: every clock-in/clock-out with its duration, work site, project code and any notes you added.',
          'Period switch: tap the funnel icon to change the view between Today, This Week, This Month and Last Month — both the activity list and the Summary update together.',
          'Summary panel: shows your working days and worked hours for the period you picked (scheduled lunch/dinner breaks are deducted from worked hours).',
          'Banners will tell you if today is a Nightwork shift or a Recovery day (no work required).',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        audience: 'workersOnly',
        text: 'You will also see your overtime here — including how much counts toward the MOM 72-hour monthly cap (shown as "Live" while the month is ongoing, or "Finalized"), plus a monthly overtime calendar below the summary.',
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Location is required to clock in. If you see "Location permission denied", enable location access for the portal in your browser/phone settings and try again.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'leave-apply',
    title: 'Applying for Leave',
    icon: 'CalendarPlus',
    color: 'blue',
    audience: 'everyone',
    summary: 'Apply for leave, attach documents, and track approval.',
    route: '/leave',
    routeLabel: 'Open Leave',
    blocks: [
      {
        type: 'steps',
        heading: 'Apply for leave',
        items: [
          'Open "Leave Management" from the sidebar.',
          'Tap "Apply for Leave" at the top, or tap "Apply" on a specific balance card.',
          'Select your leave type from the list (only the types you are eligible for are shown).',
          'Choose your start and end dates. For a half day, use the AM/PM toggle on the date.',
          'Fill in the required details for that leave type (e.g. a reason), then tap "Submit Application".',
          'You will see "Leave application submitted successfully!" and the request appears in your list as Pending.',
        ],
      },
      {
        type: 'list',
        heading: 'Documents some leave types need',
        items: [
          'Marriage Leave: unlocks only after you upload your Marriage Certificate to Profile → Documents → Family Documents and HR verifies it. Upload it ahead of time.',
          'Maternity / Paternity Leave: unlock after you upload the child\'s Birth Certificate (Paternity also needs your Marriage Certificate) to Profile → Documents → Family Documents and HR verifies it.',
          'Sick / Hospitalisation Leave: upload your Medical Certificate (photo or PDF) while applying. The system reads it automatically — keep the image clear.',
          'Compassionate Leave: upload the Death Certificate (you have up to 30 days; HR will verify it).',
          'Home Leave / Urgent Leave (workers): after approval, upload your flight ticket so HR can verify it.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Marriage, Maternity and Paternity Leave are document-gated: they will not appear in the leave type list until the matching certificate has been uploaded to your profile and verified by HR. Upload these in advance so the leave is ready when you need it.',
      },
      {
        type: 'list',
        heading: 'Know your balances',
        items: [
          'Your "Leave Balances" cards show the days available, days used, and entitlement for each leave type you have.',
          'Annual Leave needs at least 3 months of service; before that (or once it is used up) applications are submitted as Unpaid Leave — the page tells you when this applies.',
          'Off in Lieu (OIL) is used automatically (oldest first) when you apply for Annual Leave. Each OIL credit has its own expiry date shown on the card — use it before it lapses, as expired OIL cannot be claimed back.',
          'Some leave types (such as Marriage or Compassionate Leave) only appear once you become eligible or first use them.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'How your Annual Leave builds up: Annual Leave is earned as you work — it is not all granted on 1 January. You earn a portion of your yearly entitlement for each completed month of service, credited around your monthly service date (your "monthsary"), so your balance grows month by month. Once you pass your work anniversary (a full year of service) you are on your full yearly entitlement, and you earn additional days as your years of service increase. This is why your balance early on can look smaller than your headline annual figure — it keeps rising as you complete each month.',
      },
      {
        type: 'note',
        tone: 'info',
        text: 'How Annual Leave is filled automatically: when you apply for Annual Leave, the system uses your balances in a fixed order — first any Off in Lieu (OIL) you have earned (the credit closest to expiry is used first), then your Annual Leave balance, and only if those run out does the remainder become Unpaid Leave. The form shows this split before you submit (for example "2 OIL + 2 AL + 1 UL"), so you always know how many days — if any — will be unpaid.',
      },
      {
        type: 'list',
        heading: 'When to use specific leave types',
        items: [
          'External Activity: use this only when company business takes you somewhere you cannot clock in/out at a project location — for example a seminar, conference, or a company business trip. If you are working at a project or site, simply clock in and clock out there as usual instead. Choose the activity type and add the location and a short business justification.',
          'On Course: apply when you attend company-approved training or a course — enter the course/training name and the course location.',
          'Claim: Off in Lieu: claim OIL for approved work done on a rest day, Sunday or public holiday — submit within 30 days of the work date (you cannot claim for today). Note: OT-eligible workmen (workers) are paid cash overtime for weekday after-hours work, so they cannot also claim OIL for it — weekday after-hours OIL is only for non-OT staff.',
        ],
      },
      {
        type: 'list',
        heading: 'Family leave: Maternity, Paternity, Shared Parental & Childcare',
        items: [
          'Maternity / Paternity Leave is used first, before Shared Parental Leave (SPL). SPL only unlocks — and only appears in your leave type list — once your own statutory parental leave is fully used up: Maternity Leave for mothers, Paternity Leave for fathers. If you try to apply for SPL before that, the system will block it and tell you which leave to use first.',
          'These parental leave types are tied to the child\'s birth date (not the calendar year) and stay valid until they expire (within 12 months of the birth), so any balance can carry into the following year up to that expiry.',
          'Childcare Leave (CCL) is a per-calendar-year entitlement: it refreshes every year on 1 January. Unused Childcare Leave does not carry over into the next year, so plan to use it within the year it applies to.',
        ],
      },
      {
        // Worker-only: the Home Leave vs Annual Leave distinction is meaningless
        // for staff (staff have no Home Leave), so it is hidden from non-workers.
        type: 'note',
        tone: 'info',
        audience: 'workersOnly',
        text: 'Home Leave vs Annual Leave: use Home Leave when you are leaving Singapore to return to your home country. Use Annual Leave for time off while you remain in Singapore (domestic). Pick the one that matches your travel plans.',
      },
      {
        type: 'note',
        tone: 'tip',
        audience: 'workersOnly',
        text: 'A Home Leave trip can be up to 30 calendar days (or whatever you have left this year). After you pick the start date, the form fills the latest return date. Start and return both count — 24 Oct to 24 Nov is 32 days, so the latest return from 24 Oct is 22 Nov.',
      },
      {
        type: 'steps',
        heading: 'Track, edit or cancel',
        items: [
          'Each application shows a status: Pending, Approved, Auto Approved, Rejected, or Cancelled.',
          'Tap the eye icon to "View Details" and see the approval progress and who needs to approve next.',
          'You can Edit a draft, Delete a future pending request, or Cancel an approved one (your balance is restored). A reason is required when cancelling.',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'Apply early. Some leave types need advance notice — Home Leave requires 30 days. Urgent Leave has no advance-notice requirement (use it when something cannot wait). The form will warn you if you are too late.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    // Worker-only: only Home Leave / Urgent Leave can be extended, and both
    // are worker leave types — staff never see this section.
    id: 'leave-extension',
    title: 'Extending Home Leave or Urgent Leave',
    icon: 'CalendarClock',
    color: 'amber',
    audience: 'workersOnly',
    summary: 'Already overseas and need more time? Add days to your approved Home or Urgent Leave.',
    route: '/leave',
    routeLabel: 'Open Leave',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Already on an approved Home Leave or Urgent Leave and need a few more days before coming back? You can request an extension from inside the portal — no need to submit a brand-new application. Only Home Leave and Urgent Leave can be extended.',
      },
      {
        type: 'steps',
        heading: 'Request an extension',
        items: [
          'Open "Leave Management" and tap the eye icon on your approved Home Leave or Urgent Leave to view its details.',
          'Scroll to the "Home Leave / Urgent Leave Extension" section near the bottom and tap "Request Extension". (This button only appears while your leave is active.)',
          'Pick your New Return Date — the extra days run from the day after your current end date up to the new date you choose.',
          'Type a short Reason for Extension (required), then submit.',
          'You will see "Extension request submitted. Awaiting approval." It goes to the same approver(s) who handled your original leave.',
        ],
      },
      {
        type: 'list',
        heading: 'When you can request one',
        items: [
          'Your leave must already be fully approved — you cannot extend a pending or rejected application.',
          'You can only request while you are on the leave: on or after your start date and on or before your current end date. You cannot request before it begins or after it has ended.',
          'Always request on the original application, not on a previous extension.',
          'Only one extension can be pending at a time — wait for it to be approved or rejected before requesting another.',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Home Leave and Urgent Leave share a combined limit of 60 calendar days per year (reset every 1 January). The form shows how many days you have left and the latest return date you can choose — a request that would go over the limit is blocked.',
      },
      {
        type: 'steps',
        heading: 'After it is approved — upload your new flight ticket',
        items: [
          'Once approved, the extension card shows "Upload Ticket". Tap the card to open it.',
          'Upload your updated return flight ticket so HR can verify your travel.',
          'HR reviews it: the card moves to "HR Reviewing", then "Activated" once your ticket is accepted.',
        ],
      },
      {
        type: 'list',
        heading: 'Tracking your extensions',
        items: [
          'Each extension appears as its own card under the extension section, showing the new dates and a status.',
          'Statuses: "Awaiting Approval" → "Upload Ticket" (approved, ticket needed) → "HR Reviewing" (ticket submitted) → "Activated" (ticket accepted). "Rejected" or "Ticket Rejected" means it needs your attention.',
          'An "Extension" badge appears next to these requests in your leave list so you can tell them apart from your original leave.',
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'leave-calendar',
    title: 'Leave Calendar',
    icon: 'CalendarDays',
    color: 'indigo',
    audience: 'everyone',
    summary: 'See who is on leave across your department or the company.',
    route: '/leave-calendar',
    routeLabel: 'Open Leave Calendar',
    blocks: [
      {
        type: 'steps',
        heading: 'View leave schedules',
        items: [
          'Open "Leave Calendar" from the sidebar.',
          'Switch between "Department" view and "Company" view using the toggle at the top.',
          'Use the month arrows to move between months; tap "Today" to jump back to the current month.',
          'Tap any date to see who is on leave that day, grouped by department.',
        ],
      },
      {
        type: 'list',
        heading: 'Search & filter',
        items: [
          'Use the search box to find a person by name or staff ID.',
          'Use the filter (funnel) button to pick specific colleagues — your selection is remembered for next time.',
          'Only approved leave appears on the calendar. Public holidays are shaded and labelled.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'To protect privacy, leave is shown with general labels (e.g. "Out of Office", "Unavailable") rather than exact reasons. The legend at the bottom explains the colours.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'profile',
    title: 'My Profile',
    icon: 'User',
    color: 'violet',
    audience: 'everyone',
    summary: 'Keep your contact details, emergency contacts and documents up to date.',
    route: '/profile',
    routeLabel: 'Open My Profile',
    blocks: [
      {
        type: 'list',
        heading: 'What is on your profile',
        items: [
          'Tabs across the top: Personal, Employment, Compensation, Professional, Legal & Compliance, and Documents (plus Name Card / Skills Upgrading where applicable).',
          'Tap the eye icon next to sensitive fields (phone, NRIC, salary, bank) to show or hide them.',
        ],
      },
      {
        type: 'steps',
        heading: 'Update your contact details',
        items: [
          'On the "Personal" tab, tap "Edit Profile".',
          'You can update your personal phone, personal email, residential address and unit/postal code.',
          'When editing an address, enter your postal code and tap "Find Address" to auto-fill it.',
          'Tap "Save Changes" to save, or "Cancel" to discard.',
        ],
      },
      {
        type: 'steps',
        heading: 'Add an emergency contact',
        items: [
          'On the "Personal" tab, find "Emergency Contacts" and tap "Add Contact".',
          'Enter the full name, relationship, and at least one phone number (use international format, e.g. +65 9000 0000).',
          'Tap "Add Contact" to save. You can edit or delete contacts anytime.',
        ],
      },
      {
        type: 'steps',
        heading: 'Upload documents (Documents tab)',
        items: [
          'Open the "Documents" tab, then the "Document Uploads" sub-tab.',
          'Find the matching category and drag a file onto its drop zone, or tap "browse" to choose a file from your phone/computer.',
          'Files can be PDF or image (JPG/PNG), up to 5MB each. For ID cards, tap "Sample" to see a clear example photo.',
          'If the system scans your file (OCR), wait while it reads it — don\'t navigate away — then check the extracted details in the verification pop-up and submit for HR to review.',
          'Each upload shows a status: Processing, Pending HR / Pending Review, Verified, or Rejected.',
        ],
      },
      {
        type: 'list',
        heading: 'Which drop zone to use',
        items: [
          'Identity Documents — ID Card (Front), ID Card (Back), Passport, Driving Licence.',
          'Banking Documents — a bank statement/letter; this is how your bank account is updated (it is not typed in directly).',
          'Education Certificates & Professional Certificates — qualifications and professional certs (these add records to your Professional tab).',
          'Family Documents — Birth Certificates, Marriage Certificate, and the death certificate for Compassionate Leave (uploading these can unlock related leave like Childcare, Maternity, Paternity or Marriage Leave).',
          'Medical Documents and Other Documents — anything else HR asks you to provide.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Some details (name, identity numbers, salary, bank account) are maintained by HR for accuracy. To change your bank account, upload a bank document under Documents → Banking Documents rather than editing it directly.',
      },
      {
        // Worker-only Profile tab — construction classification / levy / upgrade pathways.
        type: 'list',
        heading: 'Skills Upgrading (workers)',
        audience: 'workersOnly',
        items: [
          'Workers have a "Skills Upgrading" tab on their Profile showing your construction classification: skill level, total construction experience, monthly foreign-worker levy, and source country.',
          '"Current BCA Registrations" lists your trade registrations (e.g. CoreTrade, Multi-skilling) with their expiry dates, and warns you when one is expiring soon or has expired.',
          '"Upgrade Pathways" shows the routes you could qualify for and checks each requirement (experience, salary, skills assessment), so you can see what is still needed to upgrade.',
          'This tab is read-only and for your information — speak to HR if a detail looks wrong or you want to pursue an upgrade.',
        ],
      },
      {
        type: 'steps',
        heading: 'Digital Name Card (staff & directors)',
        audience: 'nonWorker',
        items: [
          'Go to the "Legal & Compliance" tab and find "Digital Name Card Consent".',
          'Turn on the business fields you want to share (e.g. designation, department, work email/phone). Your full name is always included.',
          'Tick the acknowledgement that the selected fields will be publicly accessible via a unique link and QR code, then tap "Activate Name Card".',
          'A "Name Card" tab then appears where you can share your QR code, copy your link, pause the card, or regenerate the link.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        audience: 'nonWorker',
        text: 'The digital name card is opt-in and requires your consent — nothing is shared until you activate it. Private details (NRIC/FIN, passport, home address, personal email, salary, etc.) are never included, and you can revoke consent at any time.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'staff-directory',
    title: 'Staff Directory',
    icon: 'Users',
    color: 'teal',
    audience: 'everyone',
    summary: 'Find colleagues and explore the company structure.',
    route: '/staff-directory',
    routeLabel: 'Open Staff Directory',
    blocks: [
      {
        type: 'steps',
        heading: 'Find a colleague',
        items: [
          'Open "Staff Directory" from the sidebar.',
          'Search by name, staff ID, work email or designation.',
          'Use the filter button to narrow by department.',
          'Tap anyone to open their card with work email and phone (tap to email or call).',
        ],
      },
      {
        type: 'list',
        heading: 'Ways to view',
        items: [
          'Grid View: colleague cards (default).',
          'List View: a compact table including reporting officer.',
          'Organization Chart: see the reporting hierarchy; you can download it as a PDF.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Only work contact details are shown. Personal phone, home address and identity numbers are never visible to colleagues.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'my-assets',
    title: 'My Assets',
    icon: 'Laptop',
    color: 'amber',
    audience: 'everyone',
    summary: 'View company equipment assigned to you and handle transfers/returns.',
    route: '/my-assets',
    routeLabel: 'Open My Assets',
    blocks: [
      {
        type: 'list',
        heading: 'What you will see',
        items: [
          '"My Assets" tab: everything currently assigned to you, grouped by category (IT, Access, Fleet, etc.).',
          '"Transactions" tab: anything needing your action — and your past asset history. A badge shows how many items are waiting.',
        ],
      },
      {
        type: 'steps',
        heading: 'Acknowledge equipment given to you',
        items: [
          'Go to the "Transactions" tab.',
          'Find the item under "Needs your action" and tap "Acknowledge receipt".',
          'Read and tick the responsibility declaration, then confirm. The asset now appears under "My Assets".',
        ],
      },
      {
        type: 'steps',
        heading: 'Accept a transfer from a colleague',
        items: [
          'In "Transactions", open the incoming transfer and tap "Accept".',
          'Choose the condition (New / Good / Fair / Poor / Faulty).',
          'Add at least one photo of the item, then tap "Accept Transfer".',
        ],
      },
      {
        type: 'steps',
        heading: 'Return an asset',
        items: [
          'On "My Assets", open the item and tap "Return to Inventory".',
          'Select a reason (and add notes if needed), then tap "Request Return".',
          'The asset custodian confirms the physical return to complete it.',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'When you acknowledge or accept an asset, the system generates a signed acknowledgement letter (PDF) as your record. Open the asset and tap "View acknowledgement letter" to preview or download your copy anytime.',
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Transfers and returns expire after 7 days if not completed. Items marked "(Expiring soon!)" have under 24 hours left.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'payslips',
    title: 'Payslips',
    icon: 'FileText',
    color: 'green',
    audience: 'everyone',
    summary: 'View and download your itemised salary statements.',
    route: '/payslips',
    routeLabel: 'Open Payslips',
    blocks: [
      {
        type: 'steps',
        heading: 'View and download a payslip',
        items: [
          'Open "Payslips" from the sidebar.',
          'Under "Choose a month", pick the year and month you want (months with a payslip are highlighted).',
          'Tap "Preview" to see the full itemised breakdown (earnings, deductions, net pay).',
          'Tap "PDF" to download. You can also combine several months into one PDF using the presets (Last 3 / 6 / 12 months).',
        ],
      },
      {
        type: 'list',
        heading: 'Good to know',
        items: [
          'Use the eye icon to hide or show amounts when others might see your screen.',
          'A payslip appears only after that month\'s payroll has been approved and released — you will be notified when it is ready.',
          'The yearly totals card shows your net pay, gross, deductions and overtime for the year.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'The digital payslip portal is currently live for workers. If you are staff and see a "Coming Soon" message, your online payslips are being prepared.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'petty-cash',
    title: 'Petty Cash Claims',
    icon: 'DollarSign',
    color: 'green',
    audience: 'everyone',
    summary: 'Submit and track expense claims.',
    route: '/petty-cash',
    routeLabel: 'Open Petty Cash',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Petty Cash runs as a separate application. Tapping "Petty Cash Claims" in the sidebar signs you in automatically (single sign-on) and opens the Petty Cash app, where you submit and track your claims. If the redirect does not happen on its own, tap the "Open Petty Cash" button on the screen.',
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Dental claims (Employee Handbook Clause 8) are for confirmed staff only. During probation you can still submit other petty cash claims; dental reimbursement unlocks after confirmation (S$150/year cap).',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'transport',
    title: 'Transport',
    icon: 'Truck',
    color: 'teal',
    audience: 'transportUsers',
    translationStatus: 'english-fallback',
    summary: 'Open the separate Transport app securely from the HR Portal.',
    route: '/transport',
    routeLabel: 'Open Transport',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Transport appears in your sidebar only when Transport administration has granted access to your account. The HR Portal signs you in automatically, so you do not need a second username or password.',
      },
      {
        type: 'steps',
        heading: 'Open the Transport app',
        items: [
          'Tap "Transport" in the HR Portal sidebar.',
          'Wait while the portal confirms your access and opens the Transport app.',
          'If it does not open automatically, tap "Open Transport" on the launch screen.',
          'On Android, an installed Transport PWA may open directly. On iPhone, it may open in an in-app browser because iOS does not support cross-app PWA hand-off.',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'If Transport is missing from the sidebar or says access is unavailable, contact Transport administration. HR Portal access alone does not grant Transport access.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'ro-approvals',
    title: 'For Reporting Officers: Approving Leave',
    icon: 'CheckSquare',
    color: 'rose',
    audience: 'approvers',
    summary: 'Review and approve or reject your team\'s leave requests.',
    route: '/dashboard',
    routeLabel: 'Go to Dashboard',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Leave approvals happen on your Dashboard under "My Approvals". You will also get a notification (🔔) and a count badge on the Dashboard when requests are waiting.',
      },
      {
        type: 'steps',
        heading: 'Approve or reject a request',
        items: [
          'Open your Dashboard and find the "My Approvals" card.',
          'Tap the eye icon on a request to open "Leave Application Details".',
          'Review the dates, reason, and the "Team availability" note (it warns you if others are already on leave then).',
          'Tap the green "Approve" to approve. For multi-level workflows, it moves to the next approver.',
          'To decline, tap "Reject", enter a reason (required), then tap "Confirm Rejection".',
        ],
      },
      {
        type: 'list',
        heading: 'Special cases',
        items: [
          'National Service Leave: you cannot approve/reject — tap "Acknowledge" to confirm you are aware.',
          'Sick / medical leave: you may see "Awaiting HR Verification" with no buttons until HR verifies the document first.',
          'Tapping a notification opens the request directly. If it was already actioned, you will be told it is no longer pending.',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'For a wider view of your team\'s attendance and wellbeing, use "My Team" in the sidebar. Note: approvals are done on the Dashboard, not on My Team.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'my-team',
    title: 'My Team (Reporting Officers & HODs)',
    icon: 'UsersRound',
    color: 'teal',
    audience: 'reportingLine',
    summary: 'See your team\'s attendance, leave, 72-hour OT cap and 12-day rest-day compliance at a glance.',
    route: '/team-dashboard',
    routeLabel: 'Open My Team',
    blocks: [
      {
        type: 'list',
        heading: 'What you can see',
        items: [
          'Overview: your direct reports\' attendance and anything needing attention.',
          'Trends: attendance patterns over time.',
          'Leave Insights: who is on leave now and upcoming leave across your team.',
          'Individual: drill into a single team member\'s attendance detail.',
          'Big Five: open personality profiles for career and coaching conversations (justification required; the employee sees the access log). HODs and Deputies see the whole department; ROs see direct reports only.',
          'Overtime: your team\'s progress toward the MOM 72-hour monthly overtime cap (workmen only — see below).',
          'Rest Day: your team\'s consecutive working-day streaks against the MOM 12-day rest-day limit (see below).',
          'HODs also get a "Department" tab and can switch between "Direct Reports" and the whole department.',
        ],
      },
      {
        type: 'steps',
        heading: 'Open Rest Day or Overtime monitoring',
        items: [
          'In the sidebar, tap "My Team" (or go directly to /team-dashboard).',
          'At the top of the page, tap the "Rest Day" or "Overtime" tab.',
          'You can bookmark or share a direct link: /team-dashboard?tab=restday for rest-day monitoring, or /team-dashboard?tab=overtime for the 72-hour cap view.',
          'Tap a summary card to filter the table (e.g. "Action Required"), or tap the eye icon on a row to open that person\'s detail.',
        ],
      },
      {
        type: 'list',
        heading: 'Rest Day tab — 12-day limit (MOM Employment Act s36)',
        items: [
          'Shows how many consecutive calendar days each team member has worked without a rest day. The legal maximum is 12 — a breach only happens on the 13th consecutive working day.',
          'Status colours: OK (<7 days), Watch (7–8), Warning (9–11), Critical (12 — at the ceiling; rest required before the next working day), Violated (13+).',
          'From day 9 you receive one morning email listing everyone in your scope, including anyone already at the 12-day ceiling. A separate immediate email arrives only if someone works a 13th consecutive day. A ceiling crossed later the same day arrives as one update covering everyone who newly reached it.',
          'The table shows a "Rest by …" date when someone is approaching the limit — the latest day they can rest and stay compliant.',
          'Open a person\'s detail to see a 30-day work/rest timeline, alert history, and a projection: "Rest day required by" vs "Projected next rest day" (based on their schedule, approved leave and public holidays). If those dates do not line up, the system flags that a rest day must be assigned before the deadline.',
          'Read-only for managers — you cannot acknowledge alerts here (HR handles that on the admin Rest Day Monitoring page). Use this tab to spot problems early and plan rest days with your team.',
        ],
      },
      {
        type: 'list',
        heading: 'Overtime tab — 72-hour monthly cap (MOM Part IV)',
        items: [
          'Tracks overtime hours that count toward the MOM 72-hour monthly cap for workmen on your team. Only the overtime excess counts — regular hours on rest days and public holidays are excluded.',
          'The tab appears only if you have at least one overtime-eligible workman under your care. If you manage staff-class employees only, you will not see this tab.',
          'Pick the month from the dropdown (defaults to the current month). Use the refresh button if you need the latest figures while the month is still in progress.',
          'Status bands: OK (<48h), At Risk (48–60h), Critical (60–72h), Exceeded (>72h). During the current month you may also see "Watch" or "Projected Exceed" when the forecast suggests someone will hit the cap before month-end.',
          'Tap a row to open the daily breakdown and 12-month trend for that person. The "72h Cap OT" column is the number that matters for compliance.',
          'Workmen see their own cap progress on the Attendance page (see "Clock In & Out"). Managers use this tab to monitor the whole team.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'My Team is for insight only. To approve or reject leave, use "My Approvals" on your Dashboard (see "For Reporting Officers: Approving Leave").',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'admin-workspace',
    title: 'HR & Admin Workspace',
    icon: 'Settings',
    color: 'rose',
    audience: 'hrAdmins',
    translationStatus: 'english-fallback',
    summary: 'Run HR operations, compliance and configuration from the protected Admin Portal.',
    route: '/admin',
    routeLabel: 'Open Admin Portal',
    blocks: [
      {
        type: 'steps',
        heading: 'Enter and leave the Admin Portal',
        items: [
          'Tap your name or avatar at the top-right and choose "Admin View".',
          'The admin workspace uses red accents so you can tell privileged actions from your employee self-service view.',
          'Use the account menu and choose "Employee View" when you need to perform your own employee tasks.',
        ],
      },
      {
        type: 'list',
        heading: 'Your main operating areas',
        items: [
          'People: Employee Management, document verification, CV generation, departments and site assignments.',
          'Time & pay: Leave, Attendance, Payroll, Benefits in Kind, government reimbursement and reports.',
          'Compliance: 72-hour overtime monitoring, 12-day rest-day monitoring, policy acknowledgements and notification delivery logs.',
          'Talent & lifecycle: Learning & Development, Talent Insights, Recruitment, onboarding, offboarding, work-pass and termination workflows.',
          'Platform administration: approval workflows, handbook publishing, system monitoring, usage analytics, settings and API operations (when authorised).',
        ],
      },
      {
        type: 'list',
        heading: 'How to work a queue safely',
        items: [
          'Start from the badge or queue that needs attention, then use search and filters to narrow the records.',
          'Open the record and review its details, documents, status and timeline before acting.',
          'Use the workflow action shown on the page (approve, return, reject, verify, release or close) rather than editing data to imitate a completed step.',
          'After acting, confirm the new status and check the audit timeline or notification result when the action is sensitive.',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Admin actions can expose confidential records or trigger payroll, notifications and legal workflows. Use your own named account, record a clear reason when requested, and never share exported files outside their intended audience.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'asset-custodian-workspace',
    title: 'Asset Custodian Workspace',
    icon: 'Laptop',
    color: 'amber',
    audience: 'assetCustodians',
    translationStatus: 'english-fallback',
    summary: 'Control inventory, transfers, returns, security and fleet records.',
    route: '/asset-admin',
    routeLabel: 'Open Asset Admin Portal',
    blocks: [
      {
        type: 'steps',
        heading: 'Open the Asset Admin Portal',
        items: [
          'Tap your name or avatar at the top-right and choose "Asset Admin".',
          'Use Dashboard for the overall inventory picture, or tap "Scan" to identify an asset from its QR code.',
          'Choose "HR Portal" from the account menu when you need your personal asset acknowledgements under "My Assets".',
        ],
      },
      {
        type: 'list',
        heading: 'Inventory and movement',
        items: [
          'Assets: create and maintain the asset record, assignment, condition and supporting details.',
          'Transactions: review movement history and complete transfers only after the receiving person and physical item match.',
          'Pending Returns: inspect the returned item and condition before confirming it back into inventory.',
          'Checklists and Disposal Requests: manage onboarding/offboarding equipment and controlled disposal approvals.',
        ],
      },
      {
        type: 'list',
        heading: 'Specialist registers',
        items: [
          'Endpoint Security tracks device coverage and licensing.',
          'Fleet Maintenance and Fleet Inspections track service history, recurring checks and compliance.',
          'Vehicle Accidents and Traffic Offences retain incident, insurer, driver-declaration and fine records.',
          'Configuration, External Personnel and API Access support categories, outsourced holders and approved integrations.',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'The portal record must follow the physical asset. Verify the serial number, holder, condition and evidence before approving a receipt, transfer, return or disposal.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'self-service',
    title: 'Self-Service Hub',
    icon: 'Settings',
    color: 'blue',
    audience: 'everyone',
    summary: 'One place for requests, resources, events and more.',
    route: '/self-service',
    routeLabel: 'Open Self-Service',
    blocks: [
      {
        type: 'list',
        heading: 'What you will find',
        items: [
          'Requests & Approvals: Request Letters, Nightwork, Rest Day & PH Work, Course Requests and Overseas Travel Notice — plus role-specific cards such as Worker Trade Transfer, Work Pass Decisions and offboarding.',
          'Resources: the Employee Handbook, Company Policies and Training Materials.',
          'Events: host Meetings & Training and capture attendance.',
          'Personal Insights (voluntary & confidential): the Big Five profile.',
          'Report a Concern: Anonymous Misconduct Reporting.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Open "Self-Service" from the sidebar, then tap any card. Some cards (like Worker Trade Transfer) only appear if they apply to your role.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'company-resources',
    title: 'Handbook, Policies & Training Materials',
    icon: 'BookOpenCheck',
    color: 'indigo',
    audience: 'everyone',
    translationStatus: 'english-fallback',
    summary: 'Read current company guidance and complete required acknowledgements.',
    route: '/self-service',
    routeLabel: 'Open Self-Service Resources',
    blocks: [
      {
        type: 'list',
        heading: 'Choose the right resource',
        items: [
          'Employee Handbook: the main reference for employment terms, benefits, leave and workplace practices.',
          'Company Policies: controlled policies such as the Code of Conduct and information-security rules. Each policy shows its version and effective date.',
          'Training Materials: awareness and reference content that you can read at your own pace. These materials do not require a quiz or acknowledgement unless the page says otherwise.',
        ],
      },
      {
        type: 'steps',
        heading: 'Read and acknowledge the handbook',
        items: [
          'Open Self-Service → "Employee Handbook".',
          'Use the search box or Contents list to jump to the topic you need.',
          'When a new edition needs your acknowledgement, review the "What\'s new" summary and the changed sections.',
          'After reading, tap "I have read and acknowledge". Your acknowledged edition and date are retained.',
        ],
      },
      {
        type: 'steps',
        heading: 'Complete a policy acknowledgement',
        items: [
          'Open Self-Service → "Company Policies". The progress bar shows how many policies are complete.',
          'Open any policy marked "To acknowledge" and check its version and effective date.',
          'Read the full policy, then tap "I have read and acknowledge".',
          'After acknowledgement, you can download a signed copy. A future policy version will require a new acknowledgement.',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'Use Training Materials for learning and awareness; use Company Policies when you need the controlled rule or an acknowledgement record.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'course-requests',
    title: 'Course Requests',
    icon: 'GraduationCap',
    color: 'green',
    audience: 'everyone',
    translationStatus: 'english-fallback',
    summary: 'Request company-sponsored training and follow approval, agreement and booking steps.',
    route: '/self-service/course-requests',
    routeLabel: 'Open Course Requests',
    blocks: [
      {
        type: 'steps',
        heading: 'Submit a course request',
        items: [
          'Open Self-Service → "Course Requests" and tap "New Request".',
          'If your role allows it, choose whether the request is for yourself or for a worker you are nominating.',
          'Select the course and explain the business purpose. Add the preferred dates or sessions shown in the form.',
          'Review the details and submit. The request appears under "My requests" with its current status.',
        ],
      },
      {
        type: 'list',
        heading: 'What happens next',
        items: [
          'The normal flow is HOD review → HR review → booking. The request timeline shows who has it now.',
          'HR may approve a different provider, course date or arrangement from the one requested; open the request to see the confirmed booking.',
          'If sponsorship terms or a bond apply, the attendee must review and sign the Sponsorship Agreement before the course can be booked.',
          'Once booked, contact HR or the course coordinator if the attendee cannot attend — a confirmed booking cannot be cancelled from this page.',
        ],
      },
      {
        type: 'steps',
        heading: 'Sign a Sponsorship Agreement',
        items: [
          'Open the request marked "Sign agreement".',
          'Tap "Review & sign agreement" and check the institution, course fee and any bond duration.',
          'Accept the declaration, draw your signature and submit.',
          'You can download the acknowledged agreement from the request afterwards.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        audience: 'courseApprovers',
        text: 'HODs and HR reviewers get an "HOD approvals" tab. Open each request, check the employee, course and business purpose, then approve or reject it with a clear reason.',
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'You can cancel a request only while "Cancel request" is available. Once booking is confirmed, contact HR instead.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'travel-notice',
    title: 'Overseas Travel Notice',
    icon: 'Plane',
    color: 'teal',
    audience: 'everyone',
    translationStatus: 'english-fallback',
    summary: 'Tell HR when you travel overseas during personal time, without requesting approval.',
    route: '/self-service/travel-notice',
    routeLabel: 'Open Overseas Travel Notice',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'An Overseas Travel Notice is information for HR, not a leave application and not a request for permission. Use it when travelling on a rest day or day off so the company can contact you if needed.',
      },
      {
        type: 'steps',
        heading: 'Submit a travel notice',
        items: [
          'Open Self-Service → "Overseas Travel Notice".',
          'Choose the destination country and optional city.',
          'Enter your departure and expected return date and time.',
          'Confirm the number where HR can reach you overseas, then tap "Submit travel notice".',
        ],
      },
      {
        type: 'list',
        heading: 'Keep it accurate',
        items: [
          'The page compares your expected return with your next scheduled shift.',
          'If your return is after that shift, the notice can still be submitted, but you must also apply for leave if you will miss work.',
          'Cancel the notice if the trip will not happen.',
          'After returning to Singapore, open the active notice and tap "I have returned to Singapore".',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'A travel notice never replaces Home Leave, Annual Leave or another leave application. Submit both when the trip overlaps scheduled work.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'worker-lifecycle',
    title: 'Leaving, Offboarding & Work Pass Decisions',
    icon: 'UserMinus',
    color: 'amber',
    audience: 'lifecycleParticipants',
    translationStatus: 'english-fallback',
    summary: 'Complete the departure or worker-lifecycle actions assigned to your role.',
    route: '/self-service',
    routeLabel: 'Open Self-Service',
    blocks: [
      {
        type: 'steps',
        heading: 'If you are leaving: complete My Offboarding',
        audience: 'ownOffboarding',
        items: [
          'Open Self-Service → "My Offboarding" to see the last day currently recorded and the tasks HR has released to you.',
          'Complete each available employee task. Some tasks stay unavailable until HR or Operations records the required information.',
          'For a flight-readiness task, check the scheduled departure and ticket details before tapping "Confirm I understand".',
          'Return to this page when its badge shows a new action; not every task is released at the same time.',
        ],
      },
      {
        type: 'steps',
        heading: 'If you own a colleague\'s clearance task',
        audience: 'clearanceOwners',
        items: [
          'Open "Offboarding Tasks" from the sidebar when its badge appears.',
          'Review the departing employee, due date, task description and any handover or leave-offset details.',
          'Complete the real-world handover or clearance first, then tap "Sign off".',
          'For a leave-offset proposal, review the revised last day and choose Approve or Reject instead of using the normal sign-off action.',
        ],
      },
      {
        type: 'steps',
        heading: 'Work Pass Decisions',
        audience: 'workPassActors',
        items: [
          'Open Self-Service → "Work Pass Decisions". A worker enters the queue when their pass is within the 90-day decision window and it is your turn to act.',
          'Review the pass type, expiry date, trade, department and workflow timeline.',
          'The recommender records "Retain / renew" or "End at expiry" with a reason.',
          'The department approver can approve the recommendation or return it for correction. HR handles renewal processing after department approval.',
        ],
      },
      {
        type: 'steps',
        heading: 'Request a termination or record that a worker chose to leave',
        audience: 'terminationActors',
        items: [
          'Open Self-Service → "Termination Requests" and tap "New request".',
          'Choose "Worker chose to leave" for a resignation already communicated by the worker, or the termination option for a company-initiated request.',
          'Select the worker, enter the notice date, proposed last working day and the required reason or details.',
          'Submit for the worker/department steps and HR review. Open requests can be corrected, returned or cancelled only when those actions are shown.',
        ],
      },
      {
        type: 'steps',
        heading: 'If you receive a resignation notice',
        audience: 'resignationEmployees',
        items: [
          'Open Self-Service → "Review resignation notice" from the card or notification.',
          'Check the notice date and proposed last working day, then read the notice in your preferred available language.',
          'Confirm that you chose to leave and that the dates are correct. No drawn signature is required.',
          'If anything is wrong, use "Ask HR to review this notice" and explain the problem instead of confirming it.',
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'letters',
    title: 'Request Letters',
    icon: 'Mail',
    color: 'blue',
    audience: 'everyone',
    summary: 'Request company/HR letters and track them until they are ready.',
    route: '/self-service/letters',
    routeLabel: 'Open Request Letters',
    blocks: [
      {
        type: 'steps',
        heading: 'Request a letter',
        items: [
          'Open Self-Service → "Request Letters".',
          'Under "Create New Request", choose the Letter Type: "Employment Verification" or "Home Leave Authorization".',
          'For Employment Verification, choose the Purpose (e.g. Visa Application, Bank Loan, Tenancy Agreement); if you pick "Other", describe it.',
          'Tap "Create Request", then confirm with "Submit Request". It is sent to HR for approval.',
        ],
      },
      {
        type: 'list',
        heading: 'Track and download',
        items: [
          'Your requests appear under "My Requests" with a status: Pending Approval, Approved, Rejected or Cancelled.',
          'Once Approved, use the preview or download icons to get your PDF letter.',
          'Letters are valid for 60 days. If one expires, use "Request a new letter" to pre-fill a fresh request.',
          'You can cancel a request while it is still Pending Approval.',
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'shift-requests',
    title: 'Nightwork & Rest Day Work Requests',
    icon: 'Moon',
    color: 'blue',
    audience: 'everyone',
    summary: 'Get approval when night, rest-day or PH work will give workers rest or recovery the next morning or next day.',
    route: '/self-service/nightwork',
    routeLabel: 'Open Nightwork Requests',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'These requests are raised on behalf of a team of workers — not for yourself. Workers do not submit; they see their approved schedule under the "My Assignments" tab. (Rest Day & PH work is at Self-Service → "Rest Day & PH Work Request".)',
      },
      {
        type: 'list',
        heading: 'Who should submit',
        items: [
          'Nightwork: Trade Leaders, Managers, or anyone HR has given permission. (Reporting Officers and HODs do not submit unless they are also a Trade Leader or Manager.)',
          'Rest Day / PH Work: Trade Leaders, Managers, and Reporting Officers (anyone with direct reports), or HR-permitted users.',
          'Approvals are done by the department\'s Deputy HOD and then the HOD.',
        ],
      },
      {
        type: 'list',
        heading: 'When to request',
        items: [
          'Decision rule: request when the work will lead to rest or recovery the next morning or the next day (AM Only or Full Day). That recovery is what makes this different from ordinary overtime.',
          'Nightwork: use when the team works a night shift, or extends well past normal hours into the night, and will rest the following day. Choose "Extension" (after the regular day) or "Standard" (night replaces the day shift). If they finish late but still report for a normal full day with no recovery, do not use Nightwork — that is ordinary OT.',
          'Rest Day / PH Work: use whenever workers will work on their rest day or a public holiday. Workers are on a 6-day week (Monday to Saturday per their work schedule), so in practice the rest day is Sunday. Set recovery to Full Day or AM Only when the shift means they need rest the next morning or next day; choose None only if the next day is a normal working day.',
          'Submit ahead of time so hours, recovery and workers are approved first. You can backdate if it was urgent, but you must explain why — and dates already locked for payroll are blocked (contact HR).',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Workers always clock in and out as usual — that does not change. Recovery is the day after the nightwork or rest-day/PH work date: AM Only means return after lunch; Full Day means rest the whole day.',
      },
      {
        type: 'steps',
        heading: 'How to submit a request',
        items: [
          'Open Self-Service → "Nightwork Requests" or "Rest Day & PH Work Request", then go to the "New Request" tab.',
          'Step 1 — Project & Description: pick the project and describe the works.',
          'Step 2 — Dates & Times: choose the dates, start/end times, and the recovery day (Full Day, AM Only, or None for rest day work).',
          'Step 3 — Personnel: add the workers (search by name or staff ID) and any safety personnel.',
          'Step 4 — Review & Submit: check the summary, then tap "Submit for Approval".',
          'Track progress under "My Requests" — you will see Pending Dy HOD → Pending HOD → Approved.',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'Rest day work checks the MOM 12-day consecutive-working limit. If a worker would exceed it, you must enter an override reason to justify proceeding.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'trade-transfer',
    title: 'Worker Trade Transfer',
    icon: 'Repeat',
    color: 'blue',
    audience: 'everyone',
    summary: 'Move workers into a trade you lead, with acknowledgement from everyone involved.',
    route: '/self-service/trade-transfer',
    routeLabel: 'Open Worker Trade Transfer',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'A Trade Leader raises a transfer to bring workers into a trade they lead. Each worker is then acknowledged by the current Trade Leader, the worker, and the HOD — one at a time. HR has read-only oversight and is not part of the approval chain.',
      },
      {
        type: 'list',
        heading: 'Who and when',
        items: [
          'Who requests: a Trade Leader who will receive the workers (the incoming leader). HR admins get a read-only "All Requests" view.',
          'Who acknowledges: the worker\'s current Trade Leader, then the worker, then the HOD — in that order.',
          'When: to move one or more workers from their current trade into the trade you lead (e.g. for staffing or project needs).',
        ],
      },
      {
        type: 'steps',
        heading: 'How to request a transfer',
        items: [
          'Open Self-Service → "Worker Trade Transfer" → the "Request Transfer" tab.',
          'Choose the trade to transfer into (if you lead more than one) and the effective date (today or later).',
          'Add the workers (search by name, staff ID or trade) and enter a reason.',
          'Tap "Submit Request". Track each worker under "My Requests" — you can "Cancel Request" while it is still in progress.',
        ],
      },
      {
        type: 'steps',
        heading: 'If you are asked to acknowledge',
        items: [
          'Open the request from the notification, the Dashboard card, or the "My Inbox" tab, then tap "Review".',
          'Workers: tap "Accept" to confirm you are aware, or "Decline" to stop your transfer (a reason is required to decline).',
          'Current Trade Leader / HOD: tap "Approve" or "Reject" (a reason is required to reject).',
          'Tap "Confirm" to record your decision.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: 'Each worker is handled independently — if one is rejected, the rest of the batch continues. Once all three acknowledge, the trade change is scheduled and applied on the effective date.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'passport-return',
    title: 'Passport Return',
    icon: 'BookUser',
    color: 'blue',
    audience: 'everyone',
    summary: 'Return passports previously held by HR and record each worker\'s receipt.',
    route: '/self-service/passport-return',
    routeLabel: 'Open Passport Return',
    blocks: [
      {
        type: 'list',
        heading: 'What this campaign does',
        items: [
          'HR holds every active worker passport except the workers HR has confirmed already have theirs. Those passports are not physically returned. Malaysian WP holders are not in this campaign — their passport was never held and they do not acknowledge.',
          'Held passports are permanently returned to the worker and must not go back to company safekeeping.',
          'Workers on leave acknowledge the policy after they return. Workers who already withdrew their passport acknowledge now — no second physical handover.',
          'Physical return is never conditional on the worker signing or acknowledging terms.',
          'After acknowledgement, the worker can reopen Passport Return at any time to review the recorded confirmations and exact language version they acknowledged. A worker who acknowledged in another language can also switch the policy display to English for reference; this does not change the recorded language.',
          'The campaign automatically retires after every registered record is acknowledged or resolved by witnessed exception.',
        ],
      },
      {
        type: 'steps',
        heading: 'For workers (passport held by HR)',
        items: [
          'Wait for HR to notify you that your original passport is ready.',
          'Collect and check your original passport before confirming anything in the portal.',
          'Open Passport Return, select a language, and read the passport and overseas travel responsibilities.',
          'Confirm physical receipt and acknowledge the policy. The exact language version and timestamp are retained in the audit record.',
          'Download the PDF receipt if you want a full copy of what you confirmed. Use Download PDF — do not print the on-screen page.',
        ],
      },
      {
        type: 'steps',
        heading: 'For workers (already on Home Leave / Urgent Leave)',
        items: [
          'Keep your passport with you while on leave — no collection step.',
          'After leave ends, open Passport Return when prompted.',
          'Confirm you still hold your original passport and acknowledge the policy in your language.',
        ],
      },
      {
        type: 'steps',
        heading: 'For workers (passport already withdrawn)',
        items: [
          'If HR already released your passport for renewal or a personal matter, there is no collection step.',
          'Open Passport Return after the campaign launches.',
          'Confirm you still hold your original passport and acknowledge the policy in your language.',
        ],
      },
      {
        type: 'list',
        heading: 'For HR administrators',
        items: [
          'Confirm the inventory rule, then load the full worker roster (held vs on leave vs already withdrawn).',
          'Obtain management/legal review of the English policy and every translation before Launch.',
          'Held cohort: mark one passport ready, or mark every held passport ready when HR is prepared to hand them out. Each marked worker is notified to collect. Then record handover only after giving the original document to the worker.',
          'On-leave cohort: wait until leave ends; the record moves to “Ack due” for policy acknowledgement only.',
          'Already-withdrawn cohort: policy acknowledgement is due immediately after launch — no physical handover.',
          'If a worker who was already-with later returns the original to HR before campaign handover, use “Passport now in the safe”. That moves them to Physical return. Do not notify them, and do not ask them to acknowledge while HR holds the book.',
          'For an acknowledged record, use “View acknowledgement receipt” to inspect the worker’s recorded confirmations, the passport number and expiry frozen at acknowledgement, exact language snapshot, timestamp, and English reference translation. Download PDF produces the full multi-page recorded copy — do not print the on-screen modal.',
          'If portal acknowledgement cannot be obtained after handover, return from leave, or a prior withdrawal, record a detailed exception with a second HR administrator as witness. Do not take the passport back.',
          'Every action is retained in an immutable campaign timeline and central audit log.',
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'events',
    title: 'Meetings & Training Attendance',
    icon: 'QrCode',
    color: 'teal',
    audience: 'everyone',
    summary: 'Host an event and capture attendance with a rotating QR code.',
    route: '/self-service/events',
    routeLabel: 'Open Events',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Hosting is for staff and directors. Attendees (including external guests) just scan the QR code — no special access needed.',
      },
      {
        type: 'steps',
        heading: 'Host an event',
        items: [
          'Open Self-Service → "Meetings & Training Attendance" → tap "New event".',
          'Enter the title, type (Meeting / Training / Briefing / Other), date and time, plus an optional project and location.',
          'Tap "Create event" (it starts as a Draft), then "Activate & show QR" when you are ready.',
          'Tap "Present QR" to display the code on a phone or TV — it rotates automatically for security.',
          'When finished, tap "Close & email PDF" to stop check-ins and email the attendance record to yourself. You can "Reopen for late check-ins" if someone missed it.',
        ],
      },
      {
        type: 'steps',
        heading: 'Check in to an event',
        items: [
          'Scan the QR code with your phone camera.',
          'Employees: sign in to confirm — your details are prefilled.',
          'External guests (if allowed): choose "I\'m an external attendee" and fill in your details.',
          'Sign on your screen (a signature is required) and tap "Check in".',
        ],
      },
      {
        type: 'note',
        tone: 'tip',
        text: 'The QR refreshes every 20 seconds, so a screenshot shared later will not work — scan it live.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'whistleblowing',
    title: 'Anonymous Misconduct Reporting',
    icon: 'ShieldAlert',
    color: 'amber',
    audience: 'everyone',
    summary: 'Report misconduct, fraud or compliance breaches confidentially.',
    route: '/self-service/whistleblowing',
    routeLabel: 'Open Misconduct Reporting',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'Your report is completely anonymous. Nothing about your submission is stored on the HR portal — it goes only to an authorised third-party assessor. Your name and email are optional; add them only if you want to be contacted.',
      },
      {
        type: 'steps',
        heading: 'How to report',
        items: [
          'Open Self-Service → "Anonymous Misconduct Reporting", read the attention notice, then tap "Done".',
          'Optionally add your name/email only if you want follow-up.',
          'Fill in the required details: who is involved, the type of violation, what happened and when, where it occurred, who else is aware, and how you came to know.',
          'Attach any supporting documents if you have them (optional).',
          'Tick the certification that your information is true, then tap "Submit".',
        ],
      },
      {
        type: 'note',
        tone: 'warning',
        text: 'You will get a reference code (e.g. WB-YYYYMMDD-XXXXXX) on the confirmation screen — note it down, as there is no way to look the report up again in the portal.',
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'big-five',
    title: 'Big Five Profile',
    icon: 'Brain',
    color: 'violet',
    audience: 'everyone',
    summary: 'A voluntary, confidential personality assessment with your own results.',
    route: '/self-service/big-five',
    routeLabel: 'Open Big Five Profile',
    blocks: [
      {
        type: 'note',
        tone: 'info',
        text: 'The Big Five is completely voluntary. Your responses are encrypted. HR, your HOD, Deputy HOD, and Reporting Officer can view your individual result for career conversations — each access requires a reason that is recorded in your access log. Trade Leaders and other managers cannot see it unless they also hold one of those roles.',
      },
      {
        type: 'steps',
        heading: 'Take the assessment',
        items: [
          'Open Self-Service → "Big Five Profile" and tap "Start the assessment".',
          'Read and agree to the consent, then tap "Agree & begin".',
          'Answer 120 short statements on a 1–5 scale (Very Inaccurate → Very Accurate). Answers save automatically — you can stop and resume on any device.',
          'On the last question, tap "Submit Big Five Profile" to see your result.',
        ],
      },
      {
        type: 'list',
        heading: 'Your results & your control',
        items: [
          'See your scores across the five traits (Openness, Conscientiousness, Extraversion, Agreeableness, Neuroticism), and optionally compare with people worldwide or colleagues.',
          'Tap "Who has viewed me" to see every HR access and the reason given.',
          'You can withdraw consent and permanently delete your data at any time.',
          'You can usually retake it once every 12 months.',
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'notifications',
    title: 'Notifications & Preferences',
    icon: 'Bell',
    color: 'amber',
    audience: 'everyone',
    summary: 'Stay updated and choose how you want to be notified.',
    route: '/notifications/preferences',
    routeLabel: 'Open Notification Preferences',
    blocks: [
      {
        type: 'list',
        heading: 'Staying updated',
        items: [
          'Tap the 🔔 bell at the top-right to see your latest notifications (leave decisions, reminders, asset transfers, payslips, announcements, and more).',
          'A red dot on the bell means you have something unread.',
        ],
      },
      {
        type: 'steps',
        heading: 'Choose how you are notified',
        items: [
          'Open the 🔔 bell, then tap the settings (gear) icon to open "Notification Preferences".',
          'You will see a grid of notification types grouped by category (Leave, Attendance, Documents, Payroll, Assets, Celebrations, and more).',
          'For each type, switch the delivery channels on or off: In-App, WhatsApp, Email and Google Chat.',
          'Tap "Save Changes" to apply. Use "Cancel" to discard.',
        ],
      },
      {
        type: 'list',
        heading: 'Good to know',
        items: [
          'In-App notifications are always on and cannot be switched off.',
          'WhatsApp needs a valid phone number, and Email needs a valid email address, in your profile.',
          'A lock icon means your administrator has set that channel and it cannot be changed here.',
          '"Disabled by policy" means the company has turned that notification off for everyone.',
          'Birthday Wishes: you can opt out of the company-wide birthday email at the bottom of the page.',
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────
  {
    id: 'help',
    title: 'Need More Help?',
    icon: 'LifeBuoy',
    color: 'indigo',
    audience: 'everyone',
    summary: 'Reach the HR team if you are stuck.',
    blocks: [
      {
        type: 'list',
        heading: 'Before contacting HR',
        items: [
          'Pull down to refresh the page — it often resolves out-of-date information.',
          'Check the 🔔 notifications for anything waiting on you.',
          'Make sure you are using the latest version by closing and reopening the app from your home screen.',
        ],
      },
      {
        type: 'note',
        tone: 'info',
        text: `Still need help? Email the HR team at ${SUPPORT_EMAIL} with your Staff ID and a short description (a screenshot helps).`,
      },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────────────
// Category grouping for the guide's tab bar.
//
// Single source of truth for: category labels, which sections belong to each
// category, the order categories appear in, and the order of sections within a
// category. Topics are grouped by functional area (how an employee thinks when
// looking up "how do I do X"), ordered to roughly follow the sidebar's
// frequency-of-use flow (DashboardLayout.js).
//
// A category whose sections are all hidden for a user (via section/block
// audience rules) is automatically dropped from the tab bar by UserGuide.js.
// Every section id in USER_GUIDE_SECTIONS should appear in exactly one
// category; any that is missed still renders under the "All" tab.
export const USER_GUIDE_CATEGORIES = [
  {
    id: 'start',
    label: 'Getting Started',
    sectionIds: ['getting-started', 'dashboard', 'transport'],
  },
  {
    id: 'attendance-leave',
    label: 'Attendance & Leave',
    sectionIds: ['attendance', 'leave-apply', 'leave-extension', 'leave-calendar'],
  },
  { id: 'pay', label: 'Pay & Claims', sectionIds: ['payslips', 'petty-cash'] },
  {
    id: 'my-info',
    label: 'My Information',
    sectionIds: ['profile', 'staff-directory', 'my-assets'],
  },
  {
    id: 'requests',
    label: 'Requests & Self-Service',
    sectionIds: [
      'self-service',
      'company-resources',
      'course-requests',
      'travel-notice',
      'worker-lifecycle',
      'letters',
      'shift-requests',
      'trade-transfer',
      'passport-return',
      'events',
      'whistleblowing',
    ],
  },
  { id: 'managers', label: 'For Managers', sectionIds: ['ro-approvals', 'my-team'] },
  {
    id: 'role-workspaces',
    label: 'Role Workspaces',
    sectionIds: ['admin-workspace', 'asset-custodian-workspace'],
  },
  { id: 'about-you', label: 'About You', sectionIds: ['big-five'] },
  {
    id: 'settings-help',
    label: 'Settings & Help',
    sectionIds: ['notifications', 'help'],
  },
];
